// client/src/components/Organizations.js
// Organizations management component for superadmins

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { HeadlineLarge, LabelMedium, BodyMedium } from './Typography';

const Organizations = ({ onNavigateToOrganization }) => {
  const { userRole } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load all organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const orgsRef = collection(db, 'organizations');
        const orgsSnapshot = await getDocs(orgsRef);
        
        const orgsData = [];
        orgsSnapshot.forEach(doc => {
          orgsData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort by name
        orgsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setOrganizations(orgsData);
      } catch (error) {
        console.error('Error loading organizations:', error);
        setError('Failed to load organizations. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, []);

  const handleAddOrganization = () => {
    // Navigate to blank account page for new organization
    onNavigateToOrganization(null);
  };

  const handleEditOrganization = (organizationId) => {
    // Navigate to account page for existing organization
    onNavigateToOrganization(organizationId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <HeadlineLarge color="gray-900">Organizations</HeadlineLarge>
        <button
          onClick={handleAddOrganization}
          className="px-4 py-2 bg-blue-100 text-blue-700 border border-blue-300 rounded hover:bg-blue-200 transition-colors"
        >
          Add Organization
        </button>
      </div>

      {/* Organizations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Org Name
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                City
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                State
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {organizations.map((org) => (
              <tr key={org.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <BodyMedium color="gray-900">{org.name || 'Unnamed Organization'}</BodyMedium>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <BodyMedium color="gray-600">{org.city || '-'}</BodyMedium>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <BodyMedium color="gray-600">{org.state || '-'}</BodyMedium>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <BodyMedium color="gray-600">
                    {org.annualSubscription || '-'}
                  </BodyMedium>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <button
                    onClick={() => handleEditOrganization(org.id)}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Account
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-500 text-right">
        Showing {organizations.length} organization{organizations.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default Organizations;
